import { useQuery } from '@tanstack/react-query';

const IS_DEV = import.meta.env.DEV;

/**
 * Fetches occupied tiles for a field, polling every 5 seconds in interactive mode.
 *
 * Dev mode  : loads /dev/sponsors/{fieldType}.json
 * Production: calls GET /wp-json/web-sponsors-pitch/v1/tiles/{fieldType}
 *
 * @param {string} fieldType  - Field type slug (e.g. 'normal')
 * @param {string} mode       - 'interactive' | 'preview'
 * @param {number} excludePostId - Post ID to exclude from occupied tiles (backend editing)
 * @returns {{ occupied: Array, isLoading: boolean, isError: boolean }}
 */
export function useOccupiedTiles( fieldType, mode, excludePostId = null ) {
	const { data, isLoading, isError } = useQuery( {
		queryKey:        [ 'tiles', fieldType, excludePostId ],
		queryFn:         () => fetchTiles( fieldType, excludePostId ),
		refetchInterval: mode === 'interactive' ? 30000 : false, // Poll every 30 seconds
		staleTime:       0,
		enabled:         !! fieldType,
	} );

	return {
		occupied:  data?.occupied ?? [],
		isLoading,
		isError,
	};
}

async function fetchTiles( fieldType, excludePostId = null ) {
	if ( IS_DEV ) {
		const res = await fetch( `/dev/sponsors/${ fieldType }.json?t=${ Date.now() }` );
		if ( ! res.ok ) {
			throw new Error( `Dev: failed to load sponsors/${ fieldType }.json (${ res.status })` );
		}
		return res.json();
	}

	const base = window.WebSponsorsPitch?.restUrl ?? '/wp-json/';
	let url    = `${ base }web-sponsors-pitch/v1/tiles/${ encodeURIComponent( fieldType ) }`;

	// Add exclude_post_id query param if provided (for backend editing)
	if ( excludePostId ) {
		url += `?exclude_post_id=${ encodeURIComponent( excludePostId ) }`;
	}

	const res = await fetch( url, {
		headers: { 'X-WP-Nonce': window.WebSponsorsPitch?.nonce ?? '' },
	} );
	if ( ! res.ok ) {
		throw new Error( `Failed to fetch tiles for field '${ fieldType }': ${ res.status }` );
	}
	const data = await res.json();
	if ( ! data || ! Array.isArray( data.occupied ) ) {
		throw new Error( `Invalid occupied tiles response for '${ fieldType }'` );
	}
	return data;
}
